import type { ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';

import type { SerializableNode, SerializerProps } from '@/serializer';
import { updateNodeText } from '@/utils/updateNodeText';

import { getStructureElement } from '../../serializer/getStructureElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import type { Tag } from '../Tags.types';

interface ExtendedSerializerProps extends SerializerProps {
  node: JSONContent & SerializableNode;
  tag: Tag;
  isInsideLink?: boolean;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    tag,
    isInsideLink,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);

  const isImage = node?.type === 'image';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';
  const mergeTag = node?.attrs?.mergeTag;
  const isRootContainerImage = node?.type === 'container' && rootStyle && node?.attrs?.backgroundImage;

  if (isRootContainerImage) {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        backgroundImage: tag?.image_url,
      },
    } as JSONContent & SerializableNode;
  }

  // Swap out images
  if (isImage && mergeTag) {
    if (mergeTag === 'tag.image') {
      node = produce(node, (draft) => {
        if (draft.attrs) {
          draft.attrs.src = tag?.image_url;
          draft.attrs.alt = tag?.display;
        }
      });
    }
  }

  // // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && mergeTag) {
    if (mergeTag === 'tag.display') {
      node = updateNodeText(node, tag?.display) as JSONContent & SerializableNode;
    }
    if (mergeTag === 'tag.description') {
      node = updateNodeText(node, tag?.description) as JSONContent & SerializableNode;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} isInsideLink={isInsideLink} />;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          tag,
          isInsideLink,
        },
        [...path, i],
      ),
    ) || [];

  return getStructureElement({
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
    jsonPath: path,
  });
};

export function TagsSerializer(props: ExtendedSerializerProps) {
  return <>{serializeNode(props)}</>;
}
